import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Progress, Row, Select, Space, Statistic, Tag, Typography, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined, ReloadOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import ToolPageHeader from '../../components/ToolPageHeader';
import { useTranslation } from '../../hooks/useTranslation';
import {
  estimateMp3Bytes, MAX_AUDIO_OUTPUT_BYTES, AUDIO_BITRATES, AudioExportError, AudioExportStage,
} from '../../helpers/audioExport';
import { VideoAudioMetadata, VideoToAudioJob, VIDEO_AUDIO_EXTENSIONS, validateVideoFile } from '../../helpers/videoToAudio';
import './index.css';

const { Text, Title } = Typography;
const formatSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;
const formatDuration = (seconds: number) => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 3600).toString().padStart(2, '0')}:${Math.floor(total / 60 % 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
};
type Status = 'idle' | AudioExportStage | 'completed' | 'error';
interface Result { url: string; size: number; name: string }

const VideoToAudio: React.FC = () => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`videoToAudio.${key}`);
  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<VideoAudioMetadata | null>(null);
  const [bitrate, setBitrate] = useState(128);
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const jobRef = useRef<VideoToAudioJob | null>(null);
  const resultUrlRef = useRef('');
  const busy = ['loading', 'analyzing', 'compressing', 'finalizing'].includes(status);
  const duration = metadata?.duration;
  const estimatedBytes = duration ? estimateMp3Bytes(duration, bitrate) : 0;
  const outputTooLarge = estimatedBytes > MAX_AUDIO_OUTPUT_BYTES;

  useEffect(() => () => {
    jobRef.current?.cancel();
    jobRef.current = null;
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
  }, []);

  const clearResult = () => {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
    resultUrlRef.current = '';
    setResult(null);
  };

  const selectFile = (nextFile: File) => {
    if (jobRef.current) return;
    try {
      validateVideoFile(nextFile);
    } catch (e) {
      setError(e instanceof AudioExportError ? e.code : 'invalidFile');
      return;
    }
    setFile(nextFile);
    setMetadata(null);
    setStatus('idle');
    setError('');
    setNotice('');
    setProgress(0);
    clearResult();
  };

  const compress = async () => {
    if (!file || jobRef.current || outputTooLarge) return;
    const job = new VideoToAudioJob();
    jobRef.current = job;
    setStatus('loading');
    setError('');
    setNotice('');
    setProgress(0);
    clearResult();
    try {
      const output = await job.run(file, bitrate, (update) => {
        if (jobRef.current !== job) return;
        setStatus(update.stage);
        setProgress(update.progress);
        if (update.metadata) setMetadata(update.metadata);
      });
      if (jobRef.current !== job) return;
      const url = URL.createObjectURL(output.blob);
      resultUrlRef.current = url;
      setResult({ url, size: output.blob.size, name: `${file.name.replace(/\.[^.]+$/, '')}-audio.mp3` });
      setProgress(100);
      setStatus('completed');
    } catch (e) {
      if (jobRef.current !== job) return;
      setError(e instanceof AudioExportError ? e.code : 'compressionFailed');
      setStatus('error');
    } finally {
      if (jobRef.current === job) jobRef.current = null;
    }
  };

  const cancel = () => {
    const job = jobRef.current;
    jobRef.current = null;
    job?.cancel();
    setStatus('idle');
    setProgress(0);
    setNotice('cancelled');
  };

  const preset = [64, 128, 192].includes(bitrate) ? bitrate : 'custom';
  const uploadProps = {
    accept: VIDEO_AUDIO_EXTENSIONS.map((extension) => `.${extension}`).join(','), multiple: false, showUploadList: false, disabled: busy,
    beforeUpload: (nextFile: File) => { selectFile(nextFile); return false; },
  };

  return (
    <div className="video-to-audio-page">
      <Card className="video-to-audio-card">
        <ToolPageHeader backTo="/image-processing" backText={tr('back')}
          title={tr('title')} subtitle={tr('subtitle')} icon={<SoundOutlined aria-hidden="true" />}
          extra={<Tag color="blue">{tr('localBadge')}</Tag>} />
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={13}>
            <Card className="vta-panel">
              <div className="vta-panel-heading">
                <Title level={3}>{tr('sourceTitle')}</Title>
                {file && <Upload {...uploadProps}><Button icon={<ReloadOutlined aria-hidden="true" />} disabled={busy}>{tr('changeFile')}</Button></Upload>}
              </div>
              {!file ? <Upload.Dragger {...uploadProps} className="vta-dropzone">
                <p className="ant-upload-drag-icon"><InboxOutlined aria-hidden="true" /></p>
                <p className="ant-upload-text">{tr('uploadHint')}</p>
                <p className="ant-upload-hint">{tr('uploadLimit')}</p>
              </Upload.Dragger> : <div className="vta-source">
                <Text strong className="vta-filename">{file.name}</Text>
                <Space wrap><Text type="secondary">{formatSize(file.size)}</Text>
                  {duration != null && duration > 0 && <Tag>{formatDuration(duration)}</Tag>}
                  {metadata && <Tag>{metadata.codec.toUpperCase()} / {metadata.sampleRate} Hz / {metadata.channels} {tr('channels')}</Tag>}
                </Space>
                <Text type="secondary">{tr('firstTrack')}</Text>
                {metadata && !metadata.duration && <Text type="secondary">{tr('unknownDuration')}</Text>}
                {metadata && metadata.channels > 2 && <Text type="secondary">{tr('downmix')}</Text>}
              </div>}
              <ParagraphNote text={tr('privacy')} />
              <ParagraphNote text={tr('largeFileHint')} />
            </Card>
          </Col>
          <Col xs={24} lg={11}>
            <Card className="vta-panel">
              <Title level={3}>{tr('settings')}</Title>
              <div className="vta-setting">
                <label htmlFor="vta-preset">{tr('quality')}</label>
                <Select id="vta-preset" value={preset} disabled={busy} onChange={(value) => {
                  if (value !== 'custom') setBitrate(Number(value));
                }} options={[
                  { value: 64, label: tr('small') }, { value: 128, label: tr('balanced') },
                  { value: 192, label: tr('clear') }, { value: 'custom', label: tr('custom'), disabled: true },
                ]} />
              </div>
              <div className="vta-setting">
                <label htmlFor="vta-bitrate">{tr('bitrate')}</label>
                <Select id="vta-bitrate" value={bitrate} disabled={busy} onChange={setBitrate}
                  options={AUDIO_BITRATES.map((value) => ({ value, label: `${value} kbps` }))} />
              </div>
              <Text type="secondary">{tr('estimated')}: {estimatedBytes ? formatSize(estimatedBytes) : tr(metadata ? 'unknownEstimate' : 'afterAnalysis')}</Text>
              {outputTooLarge && <Alert type="warning" showIcon message={tr('outputTooLarge')} />}
              <div className="vta-actions">
                <Button type="primary" icon={<SoundOutlined aria-hidden="true" />} disabled={!file || busy || outputTooLarge} onClick={compress}>
                  {tr(status === 'error' ? 'retry' : 'start')}
                </Button>
                {busy && <Button icon={<StopOutlined aria-hidden="true" />} onClick={cancel}>{tr('cancel')}</Button>}
              </div>
              {busy && <div role="status" aria-live="polite">
                <Text>{tr(status)}</Text><Progress percent={progress} showInfo={status !== 'compressing' || Boolean(metadata?.duration)} status="active" />
              </div>}
              {notice && <Alert type="info" showIcon message={tr(notice)} />}
              {error && <Alert type="error" showIcon message={tr(error)} />}
            </Card>
          </Col>
        </Row>
        {result && file && <Card className="vta-panel vta-result">
          <Title level={3}>{tr('completed')}</Title>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={8}><Statistic title={tr('originalSize')} value={formatSize(file.size)} /></Col>
            <Col xs={24} sm={8}><Statistic title={tr('resultSize')} value={formatSize(result.size)} /></Col>
            <Col xs={24} sm={8}><Statistic title={tr('outputFormat')} value="MP3" /></Col>
          </Row>
          <audio controls preload="metadata" src={result.url} aria-label={tr('resultPreview')} />
          <Button type="primary" href={result.url} download={result.name} icon={<DownloadOutlined aria-hidden="true" />}>{tr('download')}</Button>
        </Card>}
      </Card>
    </div>
  );
};

const ParagraphNote: React.FC<{ text: string }> = ({ text }) => <Typography.Paragraph type="secondary" className="vta-note">{text}</Typography.Paragraph>;

export default VideoToAudio;
